import React, { useState, useEffect } from 'react';
import {
  Gauge, Settings, Zap, Timer, TrendingDown, Battery, Plus, Minus, Search, Hand, Home
} from 'lucide-react';
import styles from './GrowthTrackingTab.module.css';
import useHttp from '../../../../hook/use-http';
import { API_URL_MAIN } from '../../../../constants/driveglobalfilterinitalstates_bus';
import moment from 'moment';

const ScatterChart = ({ selectedParameter = 'Speed', isVisible = false, onParameterChange, filterdata }) => {
  const [currentParameter, setCurrentParameter] = useState(selectedParameter || 'Speed');
  const [scatterData, setScatterData] = useState([]);
  const { sendReq: fetchData } = useHttp();

  const parameterData = [
    { icon: <Gauge className={styles.paramIcon} />, title: 'Speed', endpoint: 'speed_daywise', field: 'averageOfSpeed', benchmark: 20.2 },
    { icon: <Settings className={styles.paramIcon} />, title: 'Accelerator Pedal Position', endpoint: 'accelerator_pedal_daywise', field: 'acceleratorPedalPercent', benchmark: 13.4 },
    { icon: <Zap className={styles.paramIcon} />, title: 'RPM', endpoint: 'RPM_daywise', field: 'rpm', benchmark: 10 },
    { icon: <Timer className={styles.paramIcon} />, title: 'Idling', endpoint: 'Idling_daywise', field: 'idling', benchmark: 44.6 },
    { icon: <TrendingDown className={styles.paramIcon} />, title: 'Coasting', endpoint: 'coasting_daywise', field: 'coasting', benchmark: 47.1 },
    { icon: <Battery className={styles.paramIcon} />, title: 'Regeneration', endpoint: 'regeneration_daywise', field: 'regeneration', benchmark: 19.3 }
  ];

  // Simple dynamic scale selection
  const currentParameterData = parameterData.find(p => p.title === currentParameter);
  const getScale = () => {
    if (!currentParameterData) return 30;
    return currentParameterData.benchmark > 30 ? 60 : 30;
  };

  const currentScale = getScale();

  // Simple grid values based on scale
  const getGridValues = () => {
    if (currentScale === 60) {
      return [0, 6, 12, 18, 24, 30, 36, 42, 48, 54, 60];
    } else {
      return [0, 3, 6, 9, 12, 15, 18, 21, 24, 27, 30];
    }
  };

  const gridValues = getGridValues();

  // Check if VIN is selected
  const isVinSelected = () => {
    return filterdata?.VIN_NUM && filterdata.VIN_NUM.length > 0 && filterdata.VIN_NUM[0] !== "";
  };

  // Fetch scatter data when parameter or filterdata changes
  useEffect(() => {
    // Only fetch data if VIN is selected
    if (!isVinSelected()) {
      setScatterData([]);
      return;
    }

    if (!currentParameter) return;
    const paramInfo = parameterData.find(p => p.title === currentParameter);
    if (!paramInfo) return;

    const requestBody = { filterdata };

    fetchData(
      { url: API_URL_MAIN + `growth_tracking/${paramInfo.endpoint}`, body: requestBody },
      (result) => {
        const points = (result || []).map((item, index) => {
          const achievement = parseFloat(item[paramInfo.field] || 0);
          const benchmark = paramInfo.benchmark;
          let category;
          if (achievement > benchmark + 2) category = 'above';
          else if (achievement < benchmark - 2) category = 'below';
          else category = 'trend';

          return {
            date: item.date,
            achievement: achievement.toFixed(1),
            category,
            x: 50 + index * 80,
            y: 420 - (achievement / currentScale) * 300
          };
        });
        setScatterData(points);
        console.log(points);
      }
    );
  }, [currentParameter, filterdata, currentScale]);

  // Trigger initial fetch on mount
  useEffect(() => {
    if (selectedParameter) {
      setCurrentParameter(selectedParameter);
    }
  }, [selectedParameter]);

  const handleParameterChange = (paramTitle) => {
    setCurrentParameter(paramTitle);
    if (onParameterChange) onParameterChange(paramTitle);
  };

  return (
    <div className="container-fluid px-4" id="scatter-chart-section">
      <div className="row mb-4">
        <div className="col-12">
          <div className="mb-3 text-start">
            <h3 className={`${styles.sectionTitle} mb-1`}>Scatter Chart</h3>
            <p className="text-muted small mb-0">To view a Scatter Chart, click "View Graph" on any of the parameters above.</p>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center">
              <span className="fw-bold me-3" style={{ color: '#1e3a8a' }}>Parameter:</span>
              <div className="dropdown">
                <button className="btn btn-outline-primary dropdown-toggle d-flex align-items-center" type="button" data-bs-toggle="dropdown" style={{ minWidth: '250px', justifyContent: 'space-between' }}>
                  <div className="d-flex align-items-center">
                    {currentParameterData?.icon}
                    <span className="ms-2">{currentParameter}</span>
                  </div>
                </button>
                <ul className="dropdown-menu" style={{ minWidth: '250px' }}>
                  {parameterData.map((param, index) => (
                    <li key={index}>
                      <button className="dropdown-item bg-transparent d-flex align-items-center" onClick={() => handleParameterChange(param.title)}>
                        {param.icon}
                        <span className="ms-2">{param.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="d-flex align-items-center gap-4">
              <div className="d-flex align-items-center"><div className="rounded-circle me-2" style={{ width: '12px', height: '12px', backgroundColor: '#007bff' }}></div><span className="small">Achievement Trendline</span></div>
              <div className="d-flex align-items-center"><div className="rounded-circle me-2" style={{ width: '12px', height: '12px', backgroundColor: '#dc3545' }}></div><span className="small">Below Benchmark</span></div>
              <div className="d-flex align-items-center"><div className="rounded-circle me-2" style={{ width: '12px', height: '12px', backgroundColor: '#28a745' }}></div><span className="small">Above Benchmark</span></div>
              <div className="d-flex align-items-center"><div className="rounded-circle me-2" style={{ width: '12px', height: '12px', backgroundColor: '#17a2b8' }}></div><span className="small">Benchmark</span></div>
            </div>
          </div>

          <div className="card p-4">
            <div className="position-relative">
              {!isVinSelected() ? (
                // Show placeholder when no VIN is selected
                <div className="d-flex justify-content-center align-items-center" style={{ height: '500px' }}>
                  <div className="text-center">
                    <Search size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">Please select a VIN number to view the scatter chart</h5>
                    <p className="text-muted small">Use the vehicle selection filter above to choose a VIN number</p>
                  </div>
                </div>
              ) : (
                // Show chart when VIN is selected
                <svg width="100%" height="500" viewBox="0 0 1200 500">
                  <defs>
                    <pattern id="grid" width="80" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 80 0 L 0 0 0 40" fill="none" stroke="#e5e5e5" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                  <g>
                    {gridValues.map(value => (
                      <g key={value}>
                        <text x="40" y={420 - (value / currentScale) * 300} textAnchor="end" fontSize="12" fill="#666">{value}%</text>
                        <line x1="60" y1={420 - (value / currentScale) * 300} x2="1150" y2={420 - (value / currentScale) * 300} stroke="#e5e5e5" strokeWidth="1" strokeDasharray={Math.abs(value - (currentParameterData?.benchmark || 0)) < 0.1 ? "5,5" : "none"} opacity={Math.abs(value - (currentParameterData?.benchmark || 0)) < 0.1 ? "0.8" : "0.3"} />
                      </g>
                    ))}
                  </g>
                  <line x1="50" y1={420 - (currentParameterData?.benchmark / currentScale) * 300} x2="1150" y2={420 - (currentParameterData?.benchmark / currentScale) * 300} stroke="#17a2b8" strokeWidth="2" strokeDasharray="8,4" />
                  {scatterData.map((point, index) => (
                    <circle key={index} cx={point.x} cy={point.y} r="6" fill={point.category === 'above' ? '#28a745' : point.category === 'below' ? '#dc3545' : '#007bff'} stroke="white" strokeWidth="2" />
                  ))}
                  <g>
                    {scatterData.map((point, index) => (
                      <text key={index} x={point.x} y="460" textAnchor="middle" fontSize="12" fill="#666"> {moment(point.date).format('D MMM')}</text>
                    ))}
                  </g>
                  <text x="600" y="490" textAnchor="middle" fontSize="14" fill="#333" fontWeight="bold">Day-Wise</text>
                  <text x="40" y="220" textAnchor="middle" fontSize="14" fill="#333" fontWeight="bold" transform="rotate(-90, 40, 250)">Achievement %</text>
                </svg>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScatterChart;
